import { BaseMenu, BaseMenuItem } from '@components/BaseMenu/BaseMenu';
import { ConfirmDialog } from '@components/ConfirmDialog/ConfirmDialog';
import {
  CONNECT_ACCOUNT_INTRO,
  ConnectAccountModal,
} from '@components/ConnectAccountModal/ConnectAccountModal';
import {
  ActionDrawerItem,
  MobileActionDrawer,
} from '@components/MobileActionDrawer/MobileActionDrawer';
import { SocialAccountAvatar } from '@components/SocialAccountAvatar/SocialAccountAvatar';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useReturnPath } from '@lib/hooks/useReturnPath';
import { getSocialConnector, getSocialPlatformModule } from '@lib/social/scheduling/platforms';
import { showToast } from '@lib/utils/toastmaster';
import { formatDate } from '@lib/utils/units';
import {
  AddRounded,
  LinkOffRounded,
  MoreHorizRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { SocialConnection, socialConnectionsStore } from '@stores/SocialConnections';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import css from './Publishing.module.scss';

// ── Connection row ─────────────────────────────────────────────

type ConnectionAction = 'disconnect';

const CONNECTION_ACTIONS: (ActionDrawerItem & { id: ConnectionAction })[] = [
  { id: 'disconnect', label: 'Disconnect', icon: <LinkOffRounded />, danger: true },
];

const ConnectionRow = observer(function ConnectionRow({
  connection,
}: {
  connection: SocialConnection;
}) {
  const isMobile = useIsMobile();
  const returnPath = useReturnPath();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const connector = getSocialConnector(connection.platform);
  const module = getSocialPlatformModule(connection.platform);
  const platformLabel = connector?.label ?? connection.platform;
  const accountNoun = module?.accountNoun ?? 'account';
  const name = connection.displayName ?? platformLabel;
  const needsReauth = connection.status === 'needs_reauth';

  const closeMenu = () => {
    setMenuAnchor(null);
    setDrawerOpen(false);
  };

  const handleAction = (action: ConnectionAction) => {
    closeMenu();
    switch (action) {
      case 'disconnect':
        setDisconnectOpen(true);
        break;
    }
  };

  const handleDisconnect = async () => {
    try {
      await socialConnectionsStore.disconnect(connection.id);
      trackingStore.socialAccountDisconnected({ platform: connection.platform });
      setDisconnectOpen(false);
      showToast('social-disconnect-success', { platformLabel, accountNoun });
    } catch (error) {
      showToast('social-disconnect-failed', { platformLabel, accountNoun, error });
    }
  };

  return (
    <>
      <div className={css.connectionRow}>
        <SocialAccountAvatar connection={connection} />
        <div className={css.connectionInfo}>
          <div className={css.connectionName}>
            <span className={css.name}>{name}</span>
            {needsReauth && (
              <span className={css.reauthPill}>
                <WarningAmberRounded />
                Reconnect needed
              </span>
            )}
          </div>
          <div className={css.connectionMeta}>
            {platformLabel} · Connected {formatDate(connection.createdAt)}
          </div>
        </div>
        <div className={css.connectionActions}>
          {needsReauth && connector?.available && (
            <ActionButton
              onClick={() =>
                socialConnectionsStore.connect(connector.platform, 'settings', returnPath)
              }
            >
              Reconnect
            </ActionButton>
          )}
          <IconButton
            className={css.menuButton}
            size="small"
            aria-label={`Actions for ${name}`}
            onClick={(e) => (isMobile ? setDrawerOpen(true) : setMenuAnchor(e.currentTarget))}
          >
            <MoreHorizRounded />
          </IconButton>
        </div>
      </div>

      {!isMobile && (
        <BaseMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {CONNECTION_ACTIONS.map((action) => (
            <BaseMenuItem
              key={action.id}
              icon={action.icon}
              danger={action.danger}
              onClick={() => handleAction(action.id)}
            >
              {action.label}
            </BaseMenuItem>
          ))}
        </BaseMenu>
      )}

      {isMobile && (
        <MobileActionDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          title={name}
          actions={CONNECTION_ACTIONS}
          onAction={handleAction}
        />
      )}

      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={handleDisconnect}
        title={`Disconnect ${platformLabel} ${accountNoun}`}
        confirmLabel="Disconnect"
        loadingText="Disconnecting..."
      >
        Disconnect <strong>{name}</strong> from StarZero? Any posts still scheduled for this{' '}
        {accountNoun} will be cancelled. Posts that already failed stay in your history.
        {module && ` ${module.disconnectNote}`}
      </ConfirmDialog>
    </>
  );
});

// ── Tab ────────────────────────────────────────────────────────

const ConnectionsList = observer(function ConnectionsList() {
  const store = socialConnectionsStore;

  if (store.error && !store.data) {
    return (
      <div className={css.stateRow}>
        <span>Something went wrong loading your connections. Try again later.</span>
        <ActionButton variant="secondary" onClick={() => store.update()}>
          Retry
        </ActionButton>
      </div>
    );
  }
  if (!store.data) {
    return <div className={css.stateRow}>Loading connections...</div>;
  }
  if (store.data.length === 0) {
    return (
      <div className={css.emptyState}>
        <div className={css.emptyTitle}>No connected accounts yet</div>
        <div className={css.emptySubtitle}>Connect an account above to get started.</div>
      </div>
    );
  }
  return (
    <div className={css.connectionList}>
      {store.sortedConnections.map((connection) => (
        <ConnectionRow key={connection.id} connection={connection} />
      ))}
    </div>
  );
});

/** The connect modal's return lands on this tab; `useSocialConnectReturn` in the nav shell reads the outcome. */
export const Publishing = observer(function Publishing() {
  const [connectOpen, setConnectOpen] = useState(false);
  useEffect(() => {
    // Always refetch: a `needs_reauth` flip or a disconnect elsewhere must show on the next visit.
    // Errors surface through `socialConnectionsStore.error`, rendered by ConnectionsList.
    socialConnectionsStore.update().catch(() => undefined);
  }, []);

  return (
    <div className={css.container}>
      <section className={css.section}>
        <div className={css.title}>Connect your account</div>
        <div className={css.subtitle}>{CONNECT_ACCOUNT_INTRO}</div>
        <div>
          <ActionButton variant="primary" onClick={() => setConnectOpen(true)}>
            <AddRounded />
            &nbsp;Connect new account
          </ActionButton>
        </div>
        <ConnectAccountModal
          open={connectOpen}
          onClose={() => setConnectOpen(false)}
          source="settings"
        />
      </section>

      <section className={css.section}>
        <div className={css.title}>Connections</div>
        <div className={css.subtitle}>Accounts you can publish to.</div>
        <ConnectionsList />
      </section>
    </div>
  );
});
